/**
 * ComposeEmailContext
 *
 * Global state for the slide-in Compose Email drawer. Any page can call
 * `openCompose(prefill)` to slide a compose panel in from the right edge
 * of the screen instead of navigating away to /email-hub.
 *
 * Why this lives at App level
 * --------------------------------
 * Operators reported that navigating to Email Hub just to send a single
 * email loses the context of the org they were inspecting. The drawer
 * keeps them on the same page and can be minimised to a vertical "Email"
 * tab on the right edge while they keep working.
 *
 * State shape
 * --------------------------------
 *   open        : boolean — drawer visible at all
 *   minimized   : boolean — collapsed to a right-edge tab
 *   draft       : { to_email, to_name, cc, bcc, subject, body, attachments,
 *                   org_id, contact_id, template_id, ai_polished,
 *                   nudge_enabled, nudge_1_hours, nudge_2_days, nudge_3_weeks }
 *   originOrgId : org the drawer was OPENED for. Used to warn when the
 *                 user navigates to a different org with a dirty draft.
 *
 * Dirty tracking
 * --------------------------------
 * A draft is "dirty" when subject, body, attachments or recipients have
 * been touched. Closing while dirty pops an AlertDialog instead of
 * silently discarding.
 */
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

const BLANK_DRAFT = {
  to_email: '',
  to_name: '',
  cc: '',
  bcc: '',
  subject: '',
  body: '',
  org_id: '',
  org_name: '',
  contact_id: '',
  template_id: null,
  attachments: [],
  ai_polished: false,
  nudge_enabled: false,
  nudge_1_hours: 48,
  nudge_2_days: 7,
  nudge_3_weeks: 2,
};

const ComposeEmailContext = createContext(null);

export const ComposeEmailProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [draft, setDraftRaw] = useState(BLANK_DRAFT);
  const [originOrgId, setOriginOrgId] = useState(null);
  // Pending action when we need user to confirm discarding a dirty draft.
  // Shape: { type: 'close' | 'switchOrg' | 'replace', payload? }
  const [pendingAction, setPendingAction] = useState(null);
  // We keep a baseline of the draft at open-time so dirty detection is honest
  // even when the user immediately closes a freshly-pre-filled compose.
  const baselineRef = useRef(BLANK_DRAFT);

  const isDirty = useMemo(() => {
    const base = baselineRef.current;
    return (
      (draft.subject || '') !== (base.subject || '') ||
      (draft.body || '') !== (base.body || '') ||
      (draft.cc || '') !== (base.cc || '') ||
      (draft.bcc || '') !== (base.bcc || '') ||
      (draft.to_email || '') !== (base.to_email || '') ||
      (draft.attachments?.length || 0) !== (base.attachments?.length || 0)
    );
  }, [draft]);

  const setDraft = useCallback((updater) => {
    setDraftRaw((prev) => (typeof updater === 'function' ? updater(prev) : { ...prev, ...updater }));
  }, []);

  const openCompose = useCallback((prefill) => {
    // If a dirty draft already exists, ask the user before clobbering it.
    if (open && isDirty && prefill?.org_id && prefill.org_id !== originOrgId) {
      setPendingAction({ type: 'replace', payload: prefill });
      return;
    }
    const merged = { ...BLANK_DRAFT, ...(prefill || {}) };
    baselineRef.current = merged;
    setDraftRaw(merged);
    setOriginOrgId(prefill?.org_id || null);
    setOpen(true);
    setMinimized(false);
  }, [open, isDirty, originOrgId]);

  const reset = useCallback(() => {
    setOpen(false);
    setMinimized(false);
    setDraftRaw(BLANK_DRAFT);
    setOriginOrgId(null);
    baselineRef.current = BLANK_DRAFT;
    setPendingAction(null);
  }, []);

  const closeCompose = useCallback((force = false) => {
    if (!open) return;
    if (!force && isDirty) {
      setPendingAction({ type: 'close' });
      return;
    }
    reset();
  }, [open, isDirty, reset]);

  const minimize = useCallback(() => {
    if (open) setMinimized(true);
  }, [open]);

  const restore = useCallback(() => {
    setMinimized(false);
  }, []);

  // Page-level hook to warn the user when they navigate to a different org.
  // Returns true if it's OK to switch, false if the user must wait.
  const guardOrgSwitch = useCallback((nextOrgId) => {
    if (!open) return true;
    if (!originOrgId) return true;
    if (nextOrgId === originOrgId) return true;
    if (!isDirty) return true;
    setPendingAction({ type: 'switchOrg', payload: nextOrgId });
    return false;
  }, [open, originOrgId, isDirty]);

  const confirmPending = useCallback(() => {
    if (!pendingAction) return;
    if (pendingAction.type === 'close') {
      reset();
    } else if (pendingAction.type === 'switchOrg') {
      reset();
    } else if (pendingAction.type === 'replace') {
      const merged = { ...BLANK_DRAFT, ...pendingAction.payload };
      baselineRef.current = merged;
      setDraftRaw(merged);
      setOriginOrgId(pendingAction.payload?.org_id || null);
      setOpen(true);
      setMinimized(false);
      setPendingAction(null);
      return;
    }
    setPendingAction(null);
  }, [pendingAction, reset]);

  const cancelPending = useCallback(() => {
    // If the user was prompted because they were switching orgs ("Finish
    // email"), make sure the drawer is fully visible so they can actually
    // finish — minimised state would dump them back into the same problem.
    if (pendingAction?.type === 'switchOrg') {
      setMinimized(false);
    }
    setPendingAction(null);
  }, [pendingAction]);

  const value = useMemo(() => ({
    open,
    minimized,
    draft,
    originOrgId,
    isDirty,
    pendingAction,
    openCompose,
    closeCompose,
    minimize,
    restore,
    setDraft,
    reset,
    guardOrgSwitch,
    confirmPending,
    cancelPending,
  }), [
    open, minimized, draft, originOrgId, isDirty, pendingAction,
    openCompose, closeCompose, minimize, restore, setDraft, reset,
    guardOrgSwitch, confirmPending, cancelPending,
  ]);

  return (
    <ComposeEmailContext.Provider value={value}>
      {children}
    </ComposeEmailContext.Provider>
  );
};

export const useComposeEmail = () => {
  const ctx = useContext(ComposeEmailContext);
  if (!ctx) {
    throw new Error('useComposeEmail must be used inside <ComposeEmailProvider>');
  }
  return ctx;
};

export default ComposeEmailContext;
